'use client';

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Skeleton, Collapse } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { motion, AnimatePresence } from 'framer-motion';
import MessageThroughWebsite from './message_through_website';
import { useAvailabilityStore } from '../stores/availability_store';
import { Venue } from '../../../shared/models';

interface ContactBoxProps {
    venue: Venue;
    name: string;
    email: string;
    onContactClick?: () => void;
}

const ContactBox: React.FC<ContactBoxProps> = ({ name, email, venue, onContactClick }) => {
    const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
    const [showContactInfo, setShowContactInfo] = useState(false);

    // State for MessageThroughWebsite props
    const [messageProps, setMessageProps] = useState({
        defaultDate: '',
        defaultTime: '',
        defaultMustHaves: '',
        defaultNumberOfGuests: ''
    });

    const {
        selectedDate,
        selectedSection,
        numberOfPeople,
        openBarSelected,
        totalPrice
    } = useAvailabilityStore();

    // Helper function to check if venue has an active subscription
    const hasActiveSubscription = (): boolean => {
        console.log('ContactBox - Venue Subscription Debug:', {
            venueId: venue.ID,
            venueName: venue.Name,
            hasSubscription: !!venue.Subscription,
            subscription: venue.Subscription,
            isActive: venue.Subscription?.isActive,
            paymentStatus: venue.Subscription?.paymentStatus
        });

        if (!venue.Subscription || !venue.Subscription.isActive) {
            console.log('ContactBox - No active subscription found');
            return false;
        }
        // Also check if payment status is active or trialing
        const validPaymentStatuses = ['active', 'trialing'];
        const hasActivePayment = validPaymentStatuses.includes(venue.Subscription.paymentStatus);
        console.log('ContactBox - Payment status check:', hasActivePayment, 'Status:', venue.Subscription.paymentStatus);
        return hasActivePayment;
    };

    // Update MessageThroughWebsite props
    useEffect(() => {
        // Create new props object only if any of the values have changed
        const newDate = selectedDate ? selectedDate.toLocaleDateString() : '';
        const newTime = selectedDate ?
            `${selectedDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })} - ${new Date(selectedDate.getTime() + 4 * 60 * 60 * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`
            : '';
        const newMustHaves = openBarSelected ? 'Open Bar' : '';
        const newNumberOfGuests = numberOfPeople.toString();

        // Only update if values are different to prevent re-renders
        if (
            newDate !== messageProps.defaultDate ||
            newTime !== messageProps.defaultTime ||
            newMustHaves !== messageProps.defaultMustHaves ||
            newNumberOfGuests !== messageProps.defaultNumberOfGuests
        ) {
            setMessageProps({
                defaultDate: newDate,
                defaultTime: newTime,
                defaultMustHaves: newMustHaves,
                defaultNumberOfGuests: newNumberOfGuests
            });
        }
    }, [selectedDate, selectedSection, numberOfPeople, openBarSelected, totalPrice, venue, messageProps]);

    const fetchPhoneNumber = async () => {
        // Simulate a loading delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setPhoneNumber('‪(917) 409-6289‬');
    };

    useEffect(() => {
        if (showContactInfo && !phoneNumber && hasActiveSubscription()) {
            fetchPhoneNumber();
        }
    }, [showContactInfo, phoneNumber]);

    const handlePhoneClick = () => {
        if (!phoneNumber) return;
        const cleanNumber = phoneNumber.replace(/[^0-9]/g, '');
        const url = `tel:${cleanNumber}`;
        window.location.href = url;
    };

    const handleContactButtonClick = () => {
        setShowContactInfo(!showContactInfo);

        // Call the onContactClick callback if provided and the contact info is being shown
        if (!showContactInfo && onContactClick) {
            onContactClick();
        }
    };

    return (
        <Box
            sx={{
                mt: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 3,
                boxShadow: 3,
                color: 'white',
                width: '97%',
                fontFamily: 'Inter',
                overflow: 'hidden',
            }}
        >
            <AnimatePresence>
                {!showContactInfo && (
                    <motion.div
                        initial={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Typography variant="h5" fontFamily='Roboto Condensed' sx={{ color: '#FFFFFF', mb: 1 }}>
                            {name === 'Litty Team' ? 'Direct Contact' : `Hosted by ${name}`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                            Response Rate: 90%+
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                            Response Time Average: Less than 16 hours
                        </Typography>
                    </motion.div>
                )}
            </AnimatePresence>

            <Collapse in={showContactInfo}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5" fontFamily='Roboto Condensed' sx={{ color: '#FFFFFF', mb: 1 }}>
                        {name === 'Litty Team' ? 'Direct Contact' : `Hosted by ${name}`}
                    </Typography>

                    {hasActiveSubscription() ? (
                        <>
                            <Button
                                startIcon={<EmailIcon />}
                                onClick={() => {
                                    const subject = encodeURIComponent(`${venue.Name} Inquiry from Litty`);
                                    const body = encodeURIComponent(
                                        `Hello,\n\nI'm interested in learning more about ${venue.Name}. Could you please provide additional information?\n\nThank you.`
                                    );
                                    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
                                }}
                                sx={{
                                    color: '#CCCCCC',
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    },
                                    padding: '10px 16px',
                                    borderRadius: '8px',
                                }}
                            >
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {email}
                                </Typography>
                            </Button>
                            {phoneNumber === null ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', padding: '10px 16px' }}>
                                    <PhoneIcon sx={{ color: '#AAAAAA' }} />
                                    <Skeleton
                                        variant="text"
                                        width={120}
                                        sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', ml: 2 }}
                                    />
                                </Box>
                            ) : (
                                <Button
                                    startIcon={<PhoneIcon />}
                                    onClick={handlePhoneClick}
                                    sx={{
                                        color: '#CCCCCC',
                                        justifyContent: 'flex-start',
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                        padding: '10px 16px',
                                        borderRadius: '8px',
                                        mt: 1,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                        Call {phoneNumber}
                                    </Typography>
                                </Button>
                            )}
                        </>
                    ) : (
                        <></>
                    )}

                    <Box sx={{ mt: 2, width: '100%' }}>
                        <MessageThroughWebsite
                            venue={venue}
                            {...messageProps}
                        />
                    </Box>
                </Box>
            </Collapse>

            <Button
                variant="contained"
                fullWidth
                onClick={handleContactButtonClick}
                sx={{
                    backgroundColor: '#5A4A84',
                    color: 'white',
                    fontFamily: 'Inter',
                    py: 1,
                    mt: 2,
                    fontSize: '1rem',
                    '&:hover': {
                        backgroundColor: '#4A3A74',
                    },
                }}
            >
                {showContactInfo ? 'Hide Contact Info' : 'SEE CONTACT INFO'}
            </Button>
        </Box>
    );
};

export default ContactBox;
